import PropTypes from "prop-types"
import React from "react"
import { useMutation, useQueryClient } from "react-query"
import {
  ArchiveJobTemplateAction,
  EditJobTemplateAction,
  RestoreJobTemplateAction,
} from "src/actions/job_templates"
import { useMarina } from "src/contexts/MarinaContext"

import DataTableV2 from "src/components/DataTableV2"
import Tooltip from "src/components/Tooltip"

import { archiveJobTemplate, restoreJobTemplate } from "src/api/Service"

import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import JobTemplatePreview from "./JobTemplatePreview"
import { DELETED, RESTORED } from "./amplitude_events"

const JobTemplatesTable = ({ table, status }) => {
  const queryClient = useQueryClient()
  const { marinaSlug, presentmentCurrency } = useMarina()
  const showToast = useToast()
  const tracker = useTracker()
  const isActive = status === "active"

  const { mutate: archiveTemplate } = useMutation(
    (id) => archiveJobTemplate(id),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["jobTemplates", marinaSlug])
        showToast(data.message, { type: "success" })
        tracker.trackEvent(DELETED, {})
      },
      onError: (error) => {
        showToast(error.message, { type: "error" })
      },
    }
  )

  const { mutate: restoreTemplate } = useMutation(
    (id) => restoreJobTemplate(id),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(["jobTemplates", marinaSlug])
        showToast(data.message, { type: "success" })
        tracker.trackEvent(RESTORED, {})
      },
      onError: (error) => {
        showToast(error.message, { type: "error" })
      },
    }
  )

  const COLUMNS = [
    {
      key: "name",
      header: "Template Name",
      sortable: true,
      cardRole: "title",
      width: "25%",
      render: (row) => (
        <span className="flex items-center gap-2">
          {row.name}
          {row.hasInactiveLineItems && (
            <Tooltip
              placement="top"
              variant="dark"
              text="This template contains inactive items. Open it to reactivate or remove them."
            >
              <i
                className="fa-regular fa-triangle-exclamation cursor-pointer text-yellow-600"
                aria-label="Contains inactive items"
              />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      key: "description",
      header: "Description",
      sortable: false,
      cardRole: "subtitle",
      width: "45%",
      render: (row) => <>{row.description}</>,
    },
    {
      key: "line_items",
      header: "Line Items",
      sortable: false,
      cardRole: "subtitle",
      width: "15%",
      render: (row) => (
        <>
          {row.lineItems.length > 0 ? (
            <JobTemplatePreview jobTemplate={row} />
          ) : (
            "-"
          )}
        </>
      ),
    },
    {
      key: "cost_estimate",
      header: "Estimated Cost",
      sortable: false,
      cardRole: "subtitle",
      width: "15%",
      render: (row) => {
        const hasLengthBasedItems = row.lineItems.some(
          (li) => li.product.pricingStructure === "by_length"
        )
        const subtotalEstimate = row.lineItems.reduce(
          (accumulator, current) => accumulator + current.subtotal,
          0
        )
        const display =
          subtotalEstimate > 0
            ? formatCurrencyFromMinorUnits({
                amount: subtotalEstimate,
                currency: presentmentCurrency,
              })
            : "-"

        return (
          <>
            {display}
            {hasLengthBasedItems && (
              <span className="ml-2">
                <Tooltip
                  placement="bottom"
                  maxWidth="350px"
                  variant="dark"
                  text="Estimate excludes per ft or per sqft items — these are automatically calculated when this template is applied to a specific boat on a work order."
                >
                  <i className="fa-regular fa-triangle-exclamation cursor-pointer text-gray-600" />
                </Tooltip>
              </span>
            )}
          </>
        )
      },
    },
  ]

  const activeActions = [
    ArchiveJobTemplateAction({
      onClickSingle: (row) => archiveTemplate(row.id),
    }),
    EditJobTemplateAction({
      onClickSingle: (row) => (window.location.href = row.path),
    }),
  ]

  const archivedActions = [
    RestoreJobTemplateAction({
      onClickSingle: (row) => restoreTemplate(row.id),
    }),
  ]

  if (table.isError) {
    return (
      <div className="text-muted p-5 text-center">
        <h3 className="mb-5 text-lg font-semibold">
          Error loading job templates
        </h3>
      </div>
    )
  }

  return (
    <DataTableV2
      name="job-templates-table"
      columns={COLUMNS}
      actions={isActive ? activeActions : archivedActions}
      data={table.records}
      maxHeight="none"
      hideSettings={true}
      rowConfig={{ getViewUrl: (row) => row.path }}
      emptyState={
        <span className="text-gray-500">
          {table.activeFilters.length > 0
            ? "No job templates match your search and/or filter selection, please try again"
            : "No job templates to show"}
        </span>
      }
      {...table.tableProps}
    />
  )
}

JobTemplatesTable.propTypes = {
  table: PropTypes.shape({
    isError: PropTypes.bool.isRequired,
    records: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        path: PropTypes.string.isRequired,
        lineItems: PropTypes.arrayOf(
          PropTypes.shape({
            quantity: PropTypes.string.isRequired,
            subtotal: PropTypes.number,
          })
        ).isRequired,
      })
    ).isRequired,
    activeFilters: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      })
    ).isRequired,
    tableProps: PropTypes.object.isRequired,
  }).isRequired,
  status: PropTypes.string.isRequired,
}

export default JobTemplatesTable
